import * as React from 'react';
import {useEffect, useState} from 'react';
import YouTube from 'react-youtube';

import {getVideoId} from 'features/services/utils/string-helpers';
import {ImageSizes} from 'components/organisms/Shell/image-size-enum';
import {useDeviceStore, selectIsMobile, selectIsTablet} from 'features/ui/store/useDeviceStore';

import './style.scss';

import {P} from 'components/atoms/Text/P';
import {SliderArrow} from 'components/atoms/LinksAndButtons/SliderArrow';
import {MovingStill} from 'components/atoms/Media/MovingStill';
import {getImageUrlFromCDN} from 'components/atoms/Media/AsyncImage/helper';
import {ObjectImageType} from "features/template/viewModels";
import {EpiImage} from "features/search/types";

// getImageUrlFromCDN handles Storybook internally (seeded Picsum photo when getCurrentEnv() → ""),
// so we call it directly and no longer need a component-local placeholder here.

interface Props {
    images: EpiImage[];
    startIndex?: number;
    renderLeftNav?: (onClick: () => void, disabled: boolean) => React.ReactNode;
    renderRightNav?: (onClick: () => void, disabled: boolean) => React.ReactNode;
    onSlide?: (event: number) => void;
    videoUrl?: string;
    videoDescription?: string;
    putVideoLast?: boolean;
    playVideoOnClick?: boolean;
    movingStill?: string;
    movingStillCover?: EpiImage;
}

export const ModalGallery: React.FunctionComponent<Props> = ({
    images, startIndex,
    videoUrl, videoDescription, putVideoLast, playVideoOnClick: _playVideoOnClick,
    movingStill, movingStillCover,
    renderLeftNav: _renderLeftNav, renderRightNav: _renderRightNav, onSlide: _onSlide,

}) => {

    const isMobile = useDeviceStore(selectIsMobile);
    const isTablet = useDeviceStore(selectIsTablet);

    const [galleryImages, setGalleryImages] = useState<{ original: string; thumbnail: string; description: string; originalAlt: string }[]>([]);
    const [_youtubePlayer, setYoutubePlayer] = useState<unknown>(null);
    const [hasSlides, setHasSlides] = useState<boolean>(false);
    const [noOfSlides, setNoOfSlides] = useState<number>(0);
    const [addToSlides, setAddToSlides] = useState<number>(1);

    useEffect(() => {
        if(images === null || typeof images === 'undefined') return;
        setHasSlides(images.length > 0 || typeof movingStill !== 'undefined' || videoUrl !== null);
        setNoOfSlides(images.length + (typeof movingStill !== 'undefined' ? 1 : 0) + ((videoUrl !== null && videoUrl !== undefined) ? 1 : 0));
        setAddToSlides(1 + (typeof movingStill !== 'undefined' ? 1 : 0) + ((videoUrl !== null && videoUrl !== undefined && !putVideoLast) ? 1 : 0));
        setupGallery();
    }, [images]);


    const onNavClick = (onClick: ((e: React.SyntheticEvent) => void) | null) => (e: React.SyntheticEvent) => {
        onClick?.(e);
    };

    const _settings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToScroll: 1,
        centerMode: true,
        centerPadding: '0',
        slidesToShow: 1,
        arrows: (!isMobile || isTablet),
        initialSlide: startIndex,
        nextArrow: <SliderArrow next onClick={onNavClick(null)} />,
        prevArrow: <SliderArrow onClick={onNavClick(null)} />,
    };

    const onVideoPlayerReady = (event: { target: unknown }) => {
        setYoutubePlayer(event.target);
    };
    const onRenderYoutube = (url: string, description: string | undefined) => {
        const id = getVideoId(url);

        return (
            <div className="video-wrapper modal-gallery__slide">
                <YouTube
                    videoId={id}
                    id="youtubeplayer"
                    opts={{
                        height: '669',
                        width: '1440',
                        playerVars: {
                            'origin': window.location.origin,
                            'rel': 0,
                            'modestbranding': 1,
                        }
                    }}
                    onReady={onVideoPlayerReady}
                    containerClassName="video-container"
                />
                <P className="image-gallery-description">
                    {noOfSlides > 1 && <>{putVideoLast ? noOfSlides : typeof movingStill === 'undefined' ? 1 : 2} / {noOfSlides}: </>}
                    {description}
                </P>
            </div>
        )
    };

    const onRenderMovingStill = () => {
        if(typeof movingStill === 'undefined') return;

        return (
            <div className="video-wrapper modal-gallery__slide">
                <MovingStill
                    srcset={[{src: movingStill, type: "video/mp4"}]}
                    noAutoplay
                    cover={movingStillCover}
                    playing
                />
                {noOfSlides > 1 && <P className="image-gallery-description">1 / {noOfSlides}</P>}
            </div>
        )
    };

    const setupGallery = () => {
        if((images === null || typeof images === 'undefined') && typeof movingStill === 'undefined') return;
        const tempImages : { original: string; thumbnail: string; description: string; originalAlt: string }[] = [];

        images.map((image) => {
            const noCrop = image.type === ObjectImageType.FloorPlan || image.type === ObjectImageType.AltFloorPlan || image.type === ObjectImageType.Situation;

            tempImages.push({
                original: getImageUrlFromCDN(image, ImageSizes.SIZE_1600, noCrop),
                thumbnail: image.url.indexOf('.gif') > -1 ? '' : getImageUrlFromCDN(image, ImageSizes.SIZE_300, false),
                description: image.description ? image.description : '',
                originalAlt: image.altText ? image.altText : '',
            });
        });

        setGalleryImages(tempImages);
    };

    const getDescription = (index, imgNumber) => {
        if(noOfSlides > 1 || galleryImages[index].description) return <P className="image-gallery-description">
                {noOfSlides > 1 && <>{imgNumber} / {noOfSlides}</>}
                {(galleryImages[index].description && noOfSlides > 1) && <>: </>}
                {galleryImages[index].description && galleryImages[index].description}
            </P>;
        return;
    }

    if(!hasSlides) return null;

    return (
        <div className="modal-gallery">

            <div className='modal-gallery__slider' >

                {/*noOfSlides > 1 &&
                    <div className='modal-gallery__counter'>
                        <span className='placeholder'>{noOfSlides}</span> / {noOfSlides}
                    </div>
                */}
                {/*<Slider {...settings} >*/}
                    {/* Min bostad opens the gallery with startIndex 0 even when the residence has no images. */}
                    {startIndex !== undefined && galleryImages[startIndex] &&
                        <div className="modal-gallery__slide" tabIndex={0}>
                            <img src={galleryImages[startIndex].original} alt={galleryImages[startIndex].originalAlt} />
                            {getDescription(startIndex, 1)}
                        </div>
                    }
                    {movingStill !== 'undefined' &&
                        onRenderMovingStill()
                    }
                    {(videoUrl && !putVideoLast) &&
                        onRenderYoutube(videoUrl, videoDescription)
                    }
                    {galleryImages.map((item, key) => (
                        key !== startIndex 
                        ? <div className="modal-gallery__slide" key={`slide-${key}`} tabIndex={0}>
                            <img src={item.original} alt={item.originalAlt} />
                            {getDescription(key, key + addToSlides + ((startIndex !== undefined && key < startIndex) ? 1 : 0))}
                        </div>
                        : <></>
                    ))}
                    {(videoUrl && putVideoLast) &&
                        onRenderYoutube(videoUrl, videoDescription)
                    }
                {/*</Slider>*/}
            </div>
        </div>
    )
};

